import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { MOMIOS, PATROCINADOR, PATHURLDOMAIN } from '../../constantes/constantes';
import CarrouselTeam from '../carrouselTeam/carrouselTeam';
import TurnContent from './turnContent';
import CardResultsMobile from './cardResultsMobile/cardResultsMobile';
import NotesSkeletonLoader from '../skeleton/notesSkeletonLoader';
import './abstract.sass';
import './meta.sass';

const VIEWPORT_TOP_GAP = 16;

function NewConversationButton({ onClick, isVisible }) {
  return (
    <div className="container-btn-new-conversation">
      <button
        type="button"
        className={`buscador_btn-new-conversation${isVisible ? '' : ' is-hidden'}`}
        onClick={onClick}
        title="Iniciar nueva conversación"
        aria-label="Borrar historial e iniciar nueva conversación"
        aria-hidden={!isVisible}
        tabIndex={isVisible ? undefined : -1}
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M20.707 5.826l-2.533-2.533a1 1 0 0 0-1.414 0L7.05 12.003l-.03.03L5.5 18.5l6.467-1.52.03-.03 9.71-9.71a1 1 0 0 0 0-1.414zM11.302 15.88L7.5 16.5l.62-3.802 7.593-7.593 3.182 3.182-7.593 7.593zM3 20h18v1.5H3V20z" />
        </svg>
        <span className="btn-new-conversation-text">Nueva conversación</span>
      </button>
    </div>
  );
}

function TurnNotes({ turn, isMobile, initialResults }) {
  const [showAll, setShowAll] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const hits = Array.isArray(turn.hits) ? turn.hits : [];
  const notesId = `resultadosCS-${turn.id}`;

  const sortedData = useMemo(
    () => [...hits].sort((first, second) => new Date(second.publish_date) - new Date(first.publish_date)),
    [hits]
  );

  // Mostrar todos los hits sin restricciones
  const visibleData = sortedData;

  const hasNotes = sortedData.length > 0;

  useEffect(() => {
    setShowAll(false);
    setIsTransitioning(false);
  }, [turn.hits, turn.id]);

  const handleToggle = () => {
    setIsTransitioning(true);
    window.setTimeout(() => {
      setShowAll((previous) => !previous);
      setIsTransitioning(false);
    }, 300);
  };

  if (!turn.notesReady) {
    return <NotesSkeletonLoader count={hits.length} turnId={turn.id} />;
  }

  if (turn.notesError || !hasNotes) return null;

  return (
    <section className={`list-wrapper-results${showAll ? ' expanded' : ''}`}>
      <CardResultsMobile
        data={visibleData}
        id={notesId}
        ariaLabel={`Resultados relacionados con ${turn.query}`}
      />
    </section>
  );
}

function LikeDislikeButtons({ turnId, isStreaming, answerError, turnIndex }) {
  const [likeState, setLikeState] = useState(null); // 'like', 'dislike', o null
  const [isLocked, setIsLocked] = useState(false); // Indica si se ha presionado algún botón

  const handleLikeClick = () => {
    if (isLocked) return; // Bloquear si ya se presionó alguno
    
    setLikeState('like');
    setIsLocked(true); // Bloquear ambos botones
  };

  const handleDislikeClick = () => {
    if (isLocked) return; // Bloquear si ya se presionó alguno
    
    setLikeState('dislike');
    setIsLocked(true); // Bloquear ambos botones
  };

  // No mostrar hasta que el resumen se termine de cargar
  if (isStreaming || answerError) {
    return null;
  }

  // Construir nombres de clase con índice
  const likeActiveClass = `buscador_cs_icono_like_active_${turnIndex + 1}`;
  const dislikeActiveClass = `buscador_cs_icono_dislike_active_${turnIndex + 1}`;

  return (
    <div className="summary-likes">
      <button
        className={`container-like icono-like${likeState === 'like' ? ` ${likeActiveClass}` : ''}`}
        onClick={handleLikeClick}
        disabled={isLocked}
        title={isLocked ? "Voto registrado" : "Me gusta"}
        aria-label={isLocked ? "Voto registrado" : "Marcar como útil"}
      >
        <svg className='icono' viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"> <path d="M181,90.6c-2.2-8-9.5-13.6-17.8-13.6h-42.8c-3,0-5.8-1.7-7.3-4.3-1.5-2.8-1.5-6.2.2-8.8 4.8-8.6 7.8-18 8.9-27.7.8-6.4-.5-12.9-3.7-18.5-3.2-5.7-8.8-9.5-15.2-10.5h-5c-7.7.9-13.7 7.1-14.4 14.9-.4 4.8-1.6 9.5-3.6 13.9-5.7 11.2-12.9 21.5-21.4 30.8-3.8 4.5-7 9.4-9.6 14.6-1 2.2-3.2 3.5-5.6 3.5h-19.1c-3 0-5.4 2.2-5.5 5.2v88.6c0 1.5.6 2.9 1.6 4 1.1 1 2.5 1.6 3.9 1.6 9.7.3 19.4 1.5 28.9 3.7 5.2 1.3 10.6 2.1 15.9 2.4h81.3c9.2-.8 16.1-8.9 15.3-18.1-.2-2.4-.9-4.8-2.2-6.9 9.1-4.7 12.7-15.9 8-25-.4-.8-.8-1.5-1.3-2.2 8.8-5.2 11.7-16.6 6.5-25.5-.6-.9-1.2-1.8-1.9-2.7 5.8-4.6 8.2-12.3 6.2-19.4"/> </svg>
      </button>
      <button
        className={`container-like icono-dislike${likeState === 'dislike' ? ` ${dislikeActiveClass}` : ''}`}
        onClick={handleDislikeClick}
        disabled={isLocked}
        title={isLocked ? "Voto registrado" : "No me gusta"}
        aria-label={isLocked ? "Voto registrado" : "Marcar como no útil"}
      >
        <svg className='icono' viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"> <path d="M181,90.6c-2.2-8-9.5-13.6-17.8-13.6h-42.8c-3,0-5.8-1.7-7.3-4.3-1.5-2.8-1.5-6.2.2-8.8 4.8-8.6 7.8-18 8.9-27.7.8-6.4-.5-12.9-3.7-18.5-3.2-5.7-8.8-9.5-15.2-10.5h-5c-7.7.9-13.7 7.1-14.4 14.9-.4 4.8-1.6 9.5-3.6 13.9-5.7 11.2-12.9 21.5-21.4 30.8-3.8 4.5-7 9.4-9.6 14.6-1 2.2-3.2 3.5-5.6 3.5h-19.1c-3 0-5.4 2.2-5.5 5.2v88.6c0 1.5.6 2.9 1.6 4 1.1 1 2.5 1.6 3.9 1.6 9.7.3 19.4 1.5 28.9 3.7 5.2 1.3 10.6 2.1 15.9 2.4h81.3c9.2-.8 16.1-8.9 15.3-18.1-.2-2.4-.9-4.8-2.2-6.9 9.1-4.7 12.7-15.9 8-25-.4-.8-.8-1.5-1.3-2.2 8.8-5.2 11.7-16.6 6.5-25.5-.6-.9-1.2-1.8-1.9-2.7 5.8-4.6 8.2-12.3 6.2-19.4"/> </svg>
      </button>
    </div>
  );
}

export default function Results({
  conversation = [],
  isMobile,
  initialResults,
  onNewConversation,
}) {
  const parentBase = useMemo(
    () => (PATHURLDOMAIN ? PATHURLDOMAIN.replace(/\/+$/, "") : ""),
    []
  );
  const [isNewConversationButtonVisible, setIsNewConversationButtonVisible] = useState(true);
  const lastTurnId = conversation[conversation.length - 1]?.id;

  useEffect(() => {
    const scrollDirectionThreshold = 8;
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      if (currentScrollY <= VIEWPORT_TOP_GAP) {
        setIsNewConversationButtonVisible(true);
        lastScrollY = currentScrollY;
        return;
      }

      if (Math.abs(currentScrollY - lastScrollY) < scrollDirectionThreshold) return;

      setIsNewConversationButtonVisible(currentScrollY < lastScrollY);
      lastScrollY = currentScrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useLayoutEffect(() => {
    if (!lastTurnId) return;

    const turnElement = document.querySelector(`[data-turn-id="${lastTurnId}"]`);
    if (!turnElement) return;

    const row = turnElement.closest('.conversation-row') || turnElement;
    const siteHeader = document.querySelector('.header-main, header, [class*="header"]');
    const headerOffset = siteHeader ? siteHeader.getBoundingClientRect().height : 0;
    const totalOffset = headerOffset + VIEWPORT_TOP_GAP;

    window.scrollTo({
      top: Math.max(0, window.scrollY + row.getBoundingClientRect().top - totalOffset),
      behavior: 'auto',
    });
  }, [lastTurnId]);

  if (conversation.length === 0) return null;

  return (
    <div className="results-container">
      <div className="container-full-results">
        {conversation.map((turn, index) => (
          <div className="conversation-row" key={turn.id}>
            <div className="summary-card-wrapper">
              {index === 0 && (
                <NewConversationButton
                  onClick={onNewConversation}
                  isVisible={isNewConversationButtonVisible}
                />
              )}
              <section className="summary-card conversation-card" aria-label={`Respuesta para ${turn.query}`}>
                <div className="line-upsale" />
                <div className="dots_border" />
                <TurnContent
                  turn={turn}
                  parentBase={parentBase}
                  isLast={index === conversation.length - 1}
                />
              </section>
            </div>
            {turn.games?.length > 0 && (
              <CarrouselTeam
                dataGames={turn.games}
                patrocinador={PATROCINADOR}
                flagMomios={MOMIOS}
              />
            )}
            <TurnNotes turn={turn} isMobile={isMobile} initialResults={initialResults} />

            <LikeDislikeButtons turnId={turn.id} isStreaming={turn.isStreaming} answerError={turn.answerError} turnIndex={index} />
          </div>
        ))}
      </div>
    </div>
  );
}
